// Shape of one peer's awareness state. Mirrors spec §5.2 with two simplifications
// for v1: `ready` and `isFacilitator` are unused until retro mode (Day 17+).
export interface AwarenessUser {
  id: string;
  name: string;
  colorIndex: number; // 0..7, indexes CURSOR_PALETTE
  isFacilitator: boolean;
}

export type ActiveTool =
  | 'select'
  | 'hand'
  | 'sticky'
  | 'pen'
  | 'arrow'
  | 'text'
  | 'rect'
  | 'ellipse'
  | 'frame'
  | 'image';

export interface AwarenessState {
  user: AwarenessUser;
  cursor: { x: number; y: number } | null;
  selection: string[];
  activeTool: ActiveTool;
  ready?: boolean;
}

// Quiet-by-default name list — adjective + animal. Day 22+ replaces with the
// authenticated owner / guest displayName.
const ADJECTIVES = [
  'Quiet', 'Steady', 'Patient', 'Curious', 'Calm', 'Gentle',
  'Eager', 'Bright', 'Honest', 'Plain', 'Spry', 'Mellow',
];
const ANIMALS = [
  'Otter', 'Hare', 'Fox', 'Crane', 'Heron', 'Lynx',
  'Marten', 'Moth', 'Sparrow', 'Wren', 'Roe', 'Stoat',
];

export function generateUserName(): string {
  const a = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)]!;
  const n = ANIMALS[Math.floor(Math.random() * ANIMALS.length)]!;
  return `${a} ${n}`;
}

// Stable color index for a given userId. Hash-by-charcode-sum mod 8 — matches
// the `--cursor-N` assignment described in globals.css.
export function colorIndexFor(userId: string): number {
  let sum = 0;
  for (let i = 0; i < userId.length; i++) sum += userId.charCodeAt(i);
  return sum % 8;
}

// Two-letter initials for an avatar chip. "Quiet Otter" → "QO".
export function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}
